import React, { useState } from 'react';
import { Pen, Bell, Check, PlusCircle, MessageCircle, Phone, Mail, User, DollarSign, CheckCircle, RotateCcw, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

export const ClientActionsSection = ({
  woId, workOrder, client, isAtelier, navigate,
  additionalRequests, onAdditionalRequestsChanged,
  onMarkVehicleReady, onLoadWorkOrder,
}) => {
  const [showAdditionalWork, setShowAdditionalWork] = useState(false);
  const [addWorkForm, setAddWorkForm] = useState({ description: '', estimated_cost: '' });

  const pref = client?.notification_preference || 'whatsapp';
  const isPhone = pref === 'phone';
  const isWhatsApp = pref === 'whatsapp' || pref === 'both';
  const isEmail = pref === 'email' || pref === 'both';
  const isSigned = !!workOrder.client_signature;

  const sendWhatsApp = async (type) => {
    try {
      const res = await axios.post(`${API}/notifications/whatsapp-send`, { type, work_order_id: woId });
      if (res.data.success) {
        toast.success(`Message WhatsApp envoyé à ${res.data.recipient || 'client'}`);
      } else {
        toast.error(res.data.error || res.data.detail || 'Erreur WhatsApp — Vérifiez la configuration');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur WhatsApp'); }
  };

  const sendSignatureEmail = async () => {
    try {
      const signLink = `${window.location.origin}/sign/${woId}`;
      await axios.post(`${API}/notify/send-signature-email`, { work_order_id: woId, sign_link: signLink });
      toast.success('Email de signature envoyé au client');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const sendConfirmationEmail = async () => {
    try {
      await axios.post(`${API}/notify/send-confirmation-email`, { work_order_id: woId });
      toast.success('Email de confirmation envoyé');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Manual status update for additional work requests
  const updateRequestStatus = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/additional-work-requests/${requestId}`, { status: newStatus });
      toast.success(newStatus === 'approved' ? 'Travaux acceptés' : 'Travaux refusés');
      onAdditionalRequestsChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const markVehicleReady = async () => {
    try {
      const res = await axios.post(`${API}/notifications/vehicle-ready`, { work_order_id: woId });
      if (res.data.whatsapp_sent) toast.success('WhatsApp envoyé au client !');
      else if (res.data.whatsapp_error) toast.error('Erreur WhatsApp: ' + (res.data.whatsapp_error || 'Vérifiez la configuration'));
      if (res.data.email_sent) toast.success('Email envoyé au client');
      if (res.data.preference === 'phone') toast.success('Véhicule prêt - Appeler le client : ' + (res.data.client_phone || ''));
      toast.success('Véhicule marqué comme prêt');
      onLoadWorkOrder();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const submitAdditionalWork = async () => {
    if (!addWorkForm.description) { toast.error('Description requise'); return; }
    try {
      const res = await axios.post(`${API}/additional-work-requests`, {
        work_order_id: woId, description: addWorkForm.description,
        estimated_cost: parseFloat(addWorkForm.estimated_cost) || 0,
      });
      if (res.data.whatsapp_sent) toast.success('WhatsApp envoyé au client !');
      if (res.data.email_sent) toast.success('Email envoyé au client');
      if (res.data.preference === 'phone') toast.success('Demande créée - Appeler le client');
      if (!res.data.preference || res.data.preference === 'none') toast.success('Demande créée');
      setShowAdditionalWork(false);
      setAddWorkForm({ description: '', estimated_cost: '' });
      onAdditionalRequestsChanged();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Atelier view
  if (isAtelier) {
    return (
      <Card className="p-4 rounded-xl space-y-4" data-testid="client-actions-section">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><Bell className="w-4 h-4" /> Actions</h3>
        {workOrder.status !== 'completed' && !workOrder.atelier_ready && (
          <Button className="w-full h-14 text-base font-bold bg-emerald-600 hover:bg-emerald-700 shadow-lg"
            onClick={onMarkVehicleReady} data-testid="vehicle-ready-btn">
            <Check className="mr-2 w-5 h-5" /> VEHICULE PRET
          </Button>
        )}
        {workOrder.atelier_ready && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 text-center">
            <p className="text-emerald-700 font-bold flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5" /> Vehicule marque PRET
            </p>
            <p className="text-xs text-emerald-600 mt-1">
              Par {workOrder.atelier_ready_by} — {workOrder.atelier_ready_at ? new Date(workOrder.atelier_ready_at).toLocaleString('fr-CH') : ''}
              {workOrder.delivery_note_number && <span className="ml-2 font-mono">| BL: {workOrder.delivery_note_number}</span>}
            </p>
          </div>
        )}
      </Card>
    );
  }

  // Admin/Reception view — merged Signature + Notifications
  return (
    <>
      <Card className="p-4 rounded-xl space-y-4" data-testid="client-actions-section">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <Send className="w-4 h-4" /> Communication & Signature client
        </h3>

        {/* Signature status */}
        {isSigned ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-3">
            <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-700">Signée par le client</p>
              <p className="text-xs text-emerald-600">{workOrder.signature_timestamp?.substring(0, 16).replace('T', ' à ')}</p>
            </div>
            <img src={workOrder.client_signature} alt="Signature" className="border rounded max-h-12 opacity-80" data-testid="signature-image" />
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-center gap-2">
            <Pen className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-xs text-amber-700 font-medium">En attente de signature client</p>
          </div>
        )}

        {/* Phone-only warning */}
        {isPhone && (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
              <Phone className="w-5 h-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-700">Uniquement par appel téléphonique</p>
                <p className="text-xs text-amber-600">{client?.phone || 'Pas de numéro'}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {workOrder.status !== 'completed' && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={markVehicleReady} data-testid="vehicle-ready-btn">
                  <Check className="mr-1 w-4 h-4" /> Véhicule prêt
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setShowAdditionalWork(true)} data-testid="additional-work-btn">
                <PlusCircle className="mr-1 w-4 h-4" /> Travaux supplémentaires
              </Button>
            </div>

            {!isSigned && (
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-3 animate-pulse">
                <div className="flex items-center gap-3">
                  <Pen className="w-5 h-5 text-red-600 shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-red-700">SIGNATURE PHYSIQUE</p>
                    <p className="text-xs text-red-600">Signature physique obligatoire lors du dépôt du véhicule</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-600 uppercase">Journal d'appel</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500">Heure d'appel</label>
                  <Input type="time" className="h-8 text-sm" data-testid="call-time-input"
                    onChange={async (e) => { try { await axios.post(`${API}/work-orders/${woId}/call-log`, { call_time: e.target.value, type: 'time' }); } catch {} }} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Personne de contact</label>
                  <Input defaultValue={client ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : ''} className="h-8 text-sm" data-testid="call-contact-input"
                    onBlur={async (e) => { if (e.target.value) { try { await axios.post(`${API}/work-orders/${woId}/call-log`, { contact_person: e.target.value, type: 'contact' }); } catch {} } }} />
                </div>
              </div>
              {client?.id && (
                <Button variant="outline" size="sm" className="w-full mt-1 text-blue-600 border-blue-200 hover:bg-blue-50"
                  onClick={() => navigate(`/admin/clients/${client.id}`)} data-testid="open-crm-feedback-btn">
                  <User className="w-4 h-4 mr-1" /> Ouvrir fiche CRM
                </Button>
              )}
            </div>
          </div>
        )}

        {/* WhatsApp / Email actions */}
        {!isPhone && (
          <div className="flex flex-wrap gap-2">
            {isWhatsApp && (
              <>
                <Button variant="outline" size="sm" className="text-emerald-600 hover:text-emerald-700 border-emerald-200 hover:border-emerald-300"
                  onClick={() => sendWhatsApp('confirmation')} data-testid="whatsapp-confirmation-btn">
                  <MessageCircle className="mr-1 w-4 h-4" /> Confirmation RDV
                </Button>
                {!isSigned && (
                  <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                    onClick={() => sendWhatsApp('signature')} data-testid="whatsapp-signature-btn">
                    <Pen className="mr-1 w-4 h-4" /> Signature WhatsApp
                  </Button>
                )}
              </>
            )}
            {isEmail && !isWhatsApp && (
              <>
                <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300"
                  onClick={sendConfirmationEmail} data-testid="email-confirmation-btn">
                  <Mail className="mr-1 w-4 h-4" /> Confirmation RDV (Email)
                </Button>
                {!isSigned && (
                  <Button variant="outline" size="sm" className="text-violet-600 hover:text-violet-700 border-violet-200 hover:border-violet-300"
                    onClick={sendSignatureEmail} data-testid="email-signature-btn">
                    <Pen className="mr-1 w-4 h-4" /> Signature Email
                  </Button>
                )}
              </>
            )}
            {workOrder.status !== 'completed' && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={markVehicleReady} data-testid="vehicle-ready-btn">
                <Check className="mr-1 w-4 h-4" /> Véhicule prêt
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowAdditionalWork(true)} data-testid="additional-work-btn">
              <PlusCircle className="mr-1 w-4 h-4" /> Travaux supplémentaires
            </Button>
          </div>
        )}

        {/* Remettre en atelier toggle */}
        {(workOrder.status === 'completed' || workOrder.atelier_ready) && (
          <div className="border-t border-slate-200 pt-3 mt-2">
            <Button variant="outline" size="sm" className="text-amber-600 border-amber-200 hover:bg-amber-50"
              onClick={async () => {
                try {
                  await axios.put(`${API}/work-orders/${woId}`, { atelier_visible: !workOrder.atelier_visible });
                  toast.success(workOrder.atelier_visible ? 'Fiche retirée de l\'atelier' : 'Fiche remise en atelier');
                  onLoadWorkOrder();
                } catch { toast.error('Erreur'); }
              }}
              data-testid="toggle-atelier-visible-btn">
              <RotateCcw className="mr-1 w-4 h-4" />
              {workOrder.atelier_visible ? 'Retirer de l\'atelier' : 'Remettre en atelier'}
            </Button>
          </div>
        )}
      </Card>

      {/* Additional Work Requests list */}
      {additionalRequests.length > 0 && (
        <Card className="p-4 rounded-xl space-y-3" data-testid="additional-work-list">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2"><PlusCircle className="w-4 h-4" /> Demandes de travaux supplémentaires</h3>
          <div className="space-y-2">
            {additionalRequests.map(req => (
              <div key={req.id} className="flex items-start justify-between bg-slate-50 p-3 rounded-lg text-sm">
                <div className="space-y-1">
                  <p>{req.description}</p>
                  {req.estimated_cost > 0 && <p className="text-slate-500 flex items-center gap-1"><DollarSign className="w-3 h-3" /> CHF {req.estimated_cost.toFixed(2)}</p>}
                  {req.client_response_at && <p className="text-xs text-slate-400">Réponse le {new Date(req.client_response_at).toLocaleString('fr-CH')}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {(req.status === 'pending' || !req.status) && (
                    <>
                      <button onClick={() => updateRequestStatus(req.id, 'approved')}
                        className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-medium"
                        data-testid={`approve-work-${req.id}`}>
                        Accepter
                      </button>
                      <button onClick={() => updateRequestStatus(req.id, 'rejected')}
                        className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 font-medium"
                        data-testid={`reject-work-${req.id}`}>
                        Refuser
                      </button>
                    </>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    (req.status === 'accepted' || req.status === 'approved') ? 'bg-emerald-100 text-emerald-700' :
                    (req.status === 'refused' || req.status === 'rejected') ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                  }`} data-testid={`additional-work-status-${req.id}`}>
                    {(req.status === 'accepted' || req.status === 'approved') ? 'Accepté' : (req.status === 'refused' || req.status === 'rejected') ? 'Refusé' : 'En attente'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Additional Work Dialog */}
      <Dialog open={showAdditionalWork} onOpenChange={setShowAdditionalWork}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Travaux supplémentaires</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">
              {isPhone ? 'Décrivez les travaux. Pensez à appeler le client pour confirmation.'
                : isEmail ? 'Décrivez les travaux. Un email sera envoyé au client.'
                : 'Décrivez les travaux. Un message WhatsApp sera envoyé au client.'}
            </p>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Description des travaux *</Label>
              <Textarea value={addWorkForm.description} onChange={(e) => setAddWorkForm(f => ({ ...f, description: e.target.value }))} rows={3}
                placeholder="Ex: Plaquettes de frein avant usées, remplacement nécessaire..." data-testid="additional-work-description" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Coût estimé (CHF)</Label>
              <Input type="number" value={addWorkForm.estimated_cost} onChange={(e) => setAddWorkForm(f => ({ ...f, estimated_cost: e.target.value }))} placeholder="0.00" data-testid="additional-work-cost" />
            </div>
            <Button onClick={submitAdditionalWork} className="w-full" data-testid="submit-additional-work-btn">
              {isPhone ? <><Phone className="mr-2 w-4 h-4" /> Créer la demande</>
                : isEmail ? <><Mail className="mr-2 w-4 h-4" /> Envoyer par email</>
                : <><MessageCircle className="mr-2 w-4 h-4" /> Envoyer via WhatsApp</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
